import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  getPriceUpdatePreferenceApiV1PriceUpdatePreferenceGet,
  updatePriceUpdatePreferenceApiV1PriceUpdatePreferencePut,
} from "@/client";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { ProductPricePreferenceCheckboxGroup } from "@/components/admin/UserTable/PricePreference/ProductPricePreferenceCheckboxGroup";
import {
  estimatedPricePreferenceDefaultvalues,
  estimatedPriceUpdatePreferencesSchema,
  PRODUCT_CONFIGS_FOR_ESTIMATED_PRICE_UPDATE,
} from "@/components/admin/UserTable/PricePreference/schema";

export const Route = createFileRoute("/_authenticated/price-update")({
  component: () => <PriceUpdatePreferencePage />,
});

type PriceUpdatePreferenceFormValues = z.infer<typeof estimatedPriceUpdatePreferencesSchema>;

function PriceUpdatePreferencePage() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const form = useForm<z.input<typeof estimatedPriceUpdatePreferencesSchema>, unknown, PriceUpdatePreferenceFormValues>(
    {
      resolver: zodResolver(estimatedPriceUpdatePreferencesSchema),
      defaultValues: estimatedPricePreferenceDefaultvalues,
    },
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["price-update-preference"],
    queryFn: getPriceUpdatePreferenceApiV1PriceUpdatePreferenceGet,
  });

  useEffect(() => {
    if (data) {
      form.reset(data);
    }
  }, [data, form]);

  const { mutate: updatePreference, isPending } = useMutation({
    mutationFn: (data: PriceUpdatePreferenceFormValues) =>
      updatePriceUpdatePreferenceApiV1PriceUpdatePreferencePut({ requestBody: data }),
    onSuccess: () => {
      toast({
        title: t("Success"),
        description: t("Price update preferences updated successfully"),
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div className="flex flex-col gap-2 p-2">
      <h1 className="text-2xl font-bold">{t("Price Update Preferences")}</h1>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => updatePreference(values))}
          className="space-y-4 rounded-lg border p-4"
        >
          <div className="space-y-6 p-4">
            {Object.entries(PRODUCT_CONFIGS_FOR_ESTIMATED_PRICE_UPDATE).map(([key, config]) => (
              <ProductPricePreferenceCheckboxGroup
                key={key}
                title={t(config.title)}
                basePath={`${config.path}`}
                products={config.products}
                contracts={config.contracts}
                showLongHorizonSwitch={config.path === "sparkspread"}
              />
            ))}
          </div>

          <Button type="submit" disabled={isPending} className="w-full">
            {isPending ? t("Saving...") : t("Save Preferences")}
          </Button>
        </form>
      </Form>
    </div>
  );
}
